import { INTELLIGENCE_SESSION_ERROR } from '../domain/reportsIntelligenceTypes';
import type { IntelligenceKpisResponse } from '../domain/reportsIntelligenceTypes';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import type { SalesByCashierRow } from '../../sales/public-api';
export { formatReportsLoadError } from './reportsLoadErrorFormat';
import { isReportsTechnicalLoadError } from './reportsLoadErrorFormat';
export type { ReportsDataSourceFormatOptions } from './reportsKpiDataSourceFormat';
export {
  formatReportsOperacionDataSources,
  formatReportsVentasDataSources,
  formatReportsPersonalDataSources,
  isReportsAuthorizationError,
} from './reportsKpiDataSourceFormat';
export {
  formatSaleListItemsSummary,
  spanishPaymentStatusLabel,
} from '../../sales/public-api';
export {getSalesDateRange as getReportsDateRange, getSalesDateRangeLabel as getReportsDateRangeLabel, type ReportsCustomRange, type ReportsDateRange, isSalesTimeBandsApplicable, getPreviousHomologousPeriod, startOfWeekMondayLocal} from '../../sales/public-api';

export {
  buildPaymentMethodNames,
  DEFAULT_PAYMENT_METHOD_LABELS,
  displayPaymentMethodLabel,
} from '../../sales/public-api';
import { formatMoney } from '../../shared/format/public-api';

export type IntelligenceErrorPresentation = {
  message: string;
  /** Si false, no mostrar botón Reintentar (sesión caducada). */
  showRetry: boolean;
};

/**
 * Mensajes para cierres/KPIs remotos (Edge); evita detalle técnico de Supabase en pantalla.
 * `subject` es el nombre visible del bloque ('los cierres', 'los datos de margen').
 */
export function formatIntelligenceLoadError(
  error: unknown,
  subject = 'los cierres'
): IntelligenceErrorPresentation {
  const raw = String((error as { message?: string } | undefined)?.message ?? '');

  if (raw === INTELLIGENCE_SESSION_ERROR || /\binvalid jwt\b/i.test(raw)) {
    return {
      message: 'Sesión inválida o expirada. Volvé a iniciar sesión.',
      showRetry: false,
    };
  }

  if (/edge function returned a non-2xx/i.test(raw)) {
    return {
      message: `No se pudieron cargar ${subject}. Reintentá o iniciá sesión de nuevo.`,
      showRetry: true,
    };
  }

  if (isReportsTechnicalLoadError(raw)) {
    return {
      message: `No se pudieron cargar ${subject}. Revisá conexión y reintentá.`,
      showRetry: true,
    };
  }

  if (raw.trim().length > 0) {
    return { message: raw, showRetry: true };
  }

  return { message: `No se pudieron cargar ${subject}.`, showRetry: true };
}

export function buildReportsSummaryShareLines(params: {
  /** Leyenda para la línea de ventas (p. ej. "hoy" o período seleccionado en Ventas). */
  ventasPeriodLabel?: string;
  salesTotal: number;
  salesCount: number;
  salesFiscalTotal: number;
  salesFiscalCount: number;
  salesNonFiscalTotal: number;
  salesNonFiscalCount: number;
  inventoryTotalProducts: number;
  inventoryBelowMin: number;
  syncPending: number;
  syncFailed: number;
  cashExpectedAmount?: number | null;
  cashClosingAmount?: number | null;
  cashDifference?: number | null;
}) {
  const rangeLabel = params.ventasPeriodLabel ?? 'hoy';
  const lines: string[] = [
    'Resumen Informes - Vuelto',
    '',
    `Ventas (${rangeLabel.toLowerCase()}): ${formatMoney(params.salesTotal)} - ${params.salesCount} ventas`,
    `  Fiscal: ${formatMoney(params.salesFiscalTotal)} - ${params.salesFiscalCount} ventas`,
    `  No fiscal: ${formatMoney(params.salesNonFiscalTotal)} - ${params.salesNonFiscalCount} ventas`,
    `Inventario: ${params.inventoryTotalProducts} productos, ${params.inventoryBelowMin} bajo mínimo`,
    `Sincronización: ${params.syncPending} pendientes, ${params.syncFailed} fallidos`,
  ];

  if (
    params.cashExpectedAmount != null ||
    params.cashClosingAmount != null ||
    params.cashDifference != null
  ) {
    lines.push(
      '',
      'Último cierre:',
      `  Esperado: ${formatMoney((params.cashExpectedAmount ?? 0))}`,
      `  Declarado: ${formatMoney((params.cashClosingAmount ?? 0))}`,
      `  Diferencia: ${formatMoney((params.cashDifference ?? 0))}`
    );
  }

  return lines;
}

export function buildReportsVentasShareLines(params: {
  periodLabel: string;
  salesTotal: number;
  salesCount: number;
  salesFiscalTotal: number;
  salesFiscalCount: number;
  salesNonFiscalTotal: number;
  salesNonFiscalCount: number;
  pendingPayments?: number;
  /** Costo reposición / margen (servidor); opcional si hay conexión y datos. */
  costoReposicionTotal?: number | null;
  margenReposicionTotal?: number | null;
  rentabilidadReposicionPct?: number | null;
  /** P&L completo (opcional, si expenses module activo). */
  expensesTotal?: number | null;
  netIncome?: number | null;
  grossMarginPct?: number | null;
  netIncomePct?: number | null;
}) {
  const pl = params.periodLabel;
  const lines = [
    'Ventas - Vuelto',
    '',
    `Período: ${pl}`,
    `Total: ${formatMoney(params.salesTotal)} — ${params.salesCount} ventas`,
    `Fiscal: ${formatMoney(params.salesFiscalTotal)} — ${params.salesFiscalCount} ventas`,
    `No fiscal: ${formatMoney(params.salesNonFiscalTotal)} — ${params.salesNonFiscalCount} ventas`,
    `Pagos pendientes (en el período): ${params.pendingPayments ?? 0}`,
  ];
  if (params.margenReposicionTotal != null && params.costoReposicionTotal != null && params.rentabilidadReposicionPct != null) {
    lines.push(
      '',
      'Margen estimado (costo reposición):',
      `  Costo reposición: ${formatMoney(params.costoReposicionTotal)}`,
      `  Margen: ${formatMoney(params.margenReposicionTotal)}`,
      `  % sobre ventas: ${(params.rentabilidadReposicionPct * 100).toFixed(1)}%`
    );
  }
  if (params.expensesTotal != null && params.netIncome != null) {
    lines.push(
      '',
      'Estado de resultado:',
      `  Gastos: ${formatMoney(params.expensesTotal)}`,
      `  Resultado neto: ${formatMoney(params.netIncome)}`,
      `  Margen bruto: ${params.grossMarginPct != null ? (params.grossMarginPct * 100).toFixed(1) : '—'}% · Neto: ${params.netIncomePct != null ? (params.netIncomePct * 100).toFixed(1) : '—'}%`
    );
  }
  return lines;
}

/** Ritmo de ventas a partir de primera/última venta en el rango (no son horas trabajadas). */
export function computePersonalActivityRates(
  row: Pick<SalesByCashierRow, 'salesCount' | 'firstSaleAt' | 'lastSaleAt'>,
  calendarSpanHours: number,
  options?: {
    /**
     * Si es false (semana/mes/año), no calculamos ventas/h «ventana activa»: el tiempo entre
     * primera y última venta atraviesa muchos días y no representa un turno (confunde al usuario).
     */
    includeActiveWindowSalesPerHour?: boolean;
  }
): {
  /** Solo si hay primera y última venta válidas en el rango; si no, null (no inventar ritmo). */
  salesPerHourActive: number | null;
  salesPerHourCalendar: number;
  firstTimeLabel: string;
  lastTimeLabel: string;
  hasSaleTimestamps: boolean;
} | null {
  if (row.salesCount <= 0) return null;
  const salesPerHourCalendar = row.salesCount / Math.max(1, calendarSpanHours);
  const includeActive = options?.includeActiveWindowSalesPerHour !== false;

  const firstMs = row.firstSaleAt ? new Date(row.firstSaleAt).getTime() : NaN;
  const lastMs = row.lastSaleAt ? new Date(row.lastSaleAt).getTime() : NaN;
  const hasSaleTimestamps =
    Number.isFinite(firstMs) && Number.isFinite(lastMs) && lastMs >= firstMs;

  if (!hasSaleTimestamps) {
    return {
      salesPerHourActive: null,
      salesPerHourCalendar,
      firstTimeLabel: 'No disponible',
      lastTimeLabel: 'No disponible',
      hasSaleTimestamps: false,
    };
  }

  const hoursActive = Math.max(1, (lastMs - firstMs) / 3600000);
  const salesPerHourActive =
    includeActive && hasSaleTimestamps ? row.salesCount / hoursActive : null;
  const firstTimeLabel = formatLocalDateTime(row.firstSaleAt!);
  const lastTimeLabel = formatLocalDateTime(row.lastSaleAt!);
  return {
    salesPerHourActive,
    salesPerHourCalendar,
    firstTimeLabel,
    lastTimeLabel,
    hasSaleTimestamps: true,
  };
}

export function buildReportsPersonalShareLines(params: {
  periodLabel: string;
  rows: SalesByCashierRow[];
  showFiscalInDetail: boolean;
  calendarSpanHours: number;
  /** Mismo criterio que `isSalesTimeBandsApplicable` (un solo día calendario local). */
  singleCalendarDay: boolean;
}): string[] {
  const pl = params.periodLabel;
  const header = ['Personal — Informes', '', `Período: ${pl}`, ''];
  if (params.rows.length === 0) {
    return [...header, 'Sin ventas en el período.'];
  }
  const blocks: string[] = [];
  params.rows.forEach((row, idx) => {
    const n = idx + 1;
    const lines = [
      `${n}. ${row.displayName}`,
      `   Total: ${formatMoney(row.salesTotal)} · ${row.salesCount} ventas · Ticket promedio ${formatMoney(Math.round(row.ticketAverage))} · Participación ${(row.sharePct * 100).toFixed(1)}%`,
    ];
    const detailParts: string[] = [];
    if (params.showFiscalInDetail) {
      detailParts.push(`Fiscal ${row.fiscalSalesCount}`, `No fiscal ${row.nonFiscalSalesCount}`);
    }
    detailParts.push(`Pagos pendientes ${row.pendingPayments}`);
    detailParts.push(
      `Margen estimado (reposición) ${formatMoney(row.margenReposicionTotal)}`
    );
    lines.push(`   ${detailParts.join(' · ')}`);
    const act = computePersonalActivityRates(row, params.calendarSpanHours, {
      includeActiveWindowSalesPerHour: params.singleCalendarDay,
    });
    if (act) {
      if (act.hasSaleTimestamps && act.salesPerHourActive != null) {
        lines.push(
          `   Primera venta ${act.firstTimeLabel} · Última ${act.lastTimeLabel}`,
          `   Ritmo: ${act.salesPerHourActive.toFixed(1)} ventas por hora (del día) · ${act.salesPerHourCalendar.toFixed(2)} ventas por hora (período)`
        );
      } else if (act.hasSaleTimestamps) {
        lines.push(
          `   Primera y última venta del período: ${act.firstTimeLabel} · ${act.lastTimeLabel}`,
          `   Ventas por hora (período): ${act.salesPerHourCalendar.toFixed(2)}`
        );
      } else {
        lines.push(
          `   Primera y última venta: no disponible en los datos`,
          `   Ritmo del período: ${act.salesPerHourCalendar.toFixed(2)} ventas por hora`
        );
      }
    }
    blocks.push(lines.join('\n'));
  });
  return [...header, ...blocks];
}

/** Estado de cada medio de pago en `sale_payments` (p. ej. transferencia confirmada). */
export function spanishSalePaymentLineStatus(status: string): string {
  if (status === 'confirmed') return 'Confirmado';
  if (status === 'pending') return 'Pendiente';
  if (status === 'created') return 'Iniciado';
  if (status === 'expired') return 'Vencido';
  if (status === 'approved') return 'Aprobado';
  if (status === 'failed') return 'Fallido';
  if (status === 'cancelled') return 'Anulado';
  return status;
}

export type FullProfitLoss = {
  salesTotal: number;
  costoReposicionTotal: number;
  grossMargin: number;
  grossMarginPct: number;
  expensesTotal: number;
  netIncome: number;
  netIncomePct: number;
};

/**
 * Estado de resultado completo (ERP): Ventas - Costo = Margen bruto; Margen bruto - Gastos = Resultado neto.
 * Requiere `profitLoss` (sales/expenses/grossMargin) y rentabilidad (costo reposición).
 * Si no hay costo, cae a 0.
 */
export function computeFullProfitLoss(
  profitLoss: { salesTotal: number; expensesTotal: number; grossMargin: number } | null,
  rentabilidad: IntelligenceKpisResponse['rentabilidad'] | null | undefined
): FullProfitLoss {
  const salesTotal = profitLoss?.salesTotal ?? 0;
  const costoReposicionTotal = rentabilidad?.costoReposicionTotal ?? 0;
  const rawMargin = salesTotal - costoReposicionTotal;
  const grossMargin = Math.max(0, rawMargin);
  const expensesTotal = profitLoss?.expensesTotal ?? 0;
  const netIncome = rawMargin - expensesTotal;
  return {
    salesTotal,
    costoReposicionTotal,
    grossMargin,
    grossMarginPct: salesTotal > 0 ? grossMargin / salesTotal : 0,
    expensesTotal,
    netIncome,
    netIncomePct: salesTotal > 0 ? netIncome / salesTotal : 0,
  };
}

/**
 * Share de gastos sobre ventas para la tarjeta KPI de gastos.
 * Con ventas = 0 el cociente no existe — devuelve '—' en vez de una magnitud
 * ficticia (antes `Math.max(1, sales)` inflaba gastos a % astronómicos).
 */
export function expensesShareOfSalesLabel(expensesTotal: number, salesTotal: number): string {
  if (salesTotal <= 0) return '— de ventas';
  return `${((expensesTotal / salesTotal) * 100).toFixed(1)}% de ventas`;
}
